import React, { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info } from "lucide-react";
import { createPackageIncident } from "../../api/packages";
import { showToast } from "../../utils/showToast";
import Spinner from "../spinner/spinner";
import {
  INCIDENT_SEVERITY,
  INCIDENT_RESPONSIBLE,
  INCIDENT_TYPES_BY_CATEGORY,
  INCIDENT_CATEGORIES,
  getAutoSeverity,
  getRequiredDepartments,
  DEPARTMENT_LABELS,
} from "../../constants/incidents";
// El formulario conserva las clases `package-logbook-form__*` de cuando vivía
// dentro de la Auditoría del pedido: se extrajo sin cambio visible.
import "../packageLogbook/PackageLogbook.scss";
import DeskIncidentForm from "./DeskIncidentForm";

/**
 * Alta de una incidencia de paquete.
 *
 * No decide permisos: eso lo hace el contenedor (el backend responde 403 de
 * todos modos).
 *
 * Dos variantes, por decisión de producto: la "Auditoría" del pedido
 * (PackageLogbook) conserva el formulario de siempre, y el formulario mejorado
 * se usa solo en la vista "Atención e incidencias".
 *
 * @param {object} props
 * @param {'classic'|'desk'} [props.variant='classic'] - `classic`: el
 *   formulario de la Auditoría, sin ningún cambio. `desk`: selector de tipo con
 *   recientes y búsqueda sin tildes, errores en línea (sin toast de error),
 *   clave de idempotencia por huella del payload y timeout de 15 s.
 * @param {string} props.packageId - _id del paquete.
 * @param {object} [props.origin] - Opaco para el formulario (p. ej.
 *   `{ source: 'helpdesk', conversationId, ticketNumber? }`); viaja en el body
 *   solo si viene.
 * @param {(incident: object) => void} [props.onCreated] - Se llama después de
 *   crear, con la incidencia que devolvió el backend.
 * @param {boolean} [props.hidden=false] - No renderiza nada pero conserva lo
 *   escrito. PackageLogbook lo usa al cambiar de pestaña para no perder el
 *   borrador, como cuando el estado vivía en él.
 * @param {string|number} [props.followingNumber] - Solo `desk`: el título pasa
 *   a "Nueva incidencia · Pedido #N" y el aviso de éxito nombra el paquete
 *   (evita crear en el pedido equivocado). El contenedor no repite el título.
 * @param {string[]} [props.excludeTypes=[]] - Solo `desk`: tipos que no se
 *   ofrecen (la vista pasa ['apelacion_calidad']).
 * @param {() => void} [props.onCancel] - Solo `desk`: sin él no hay botón
 *   Cancelar.
 * @param {(info: { packageId: string }) => void} [props.onUncertainOutcome] -
 *   Solo `desk`: se llama cada vez que un envío termina sin saber si la
 *   incidencia se creó (red caída, timeout, 5xx, o un 429/408/401 al
 *   reintentar uno así). El formulario le pide al agente revisar "Incidencias
 *   de este pedido"; el contenedor refresca esa lista acá (CONSISTENCY 7: antes
 *   de que se cree otra con un payload distinto, la lista tiene que estar al
 *   día). No se llama tras un éxito ni tras un rechazo que aclara el resultado.
 */
export default function IncidentForm({ variant = "classic", ...props }) {
  if (variant === "desk") {
    return <DeskIncidentForm {...props} />;
  }
  return <ClassicIncidentForm {...props} />;
}

function ClassicIncidentForm({ packageId, origin, onCreated, hidden = false }) {
  const { t } = useTranslation();
  const fieldId = useId();

  const [incidentSeverity, setIncidentSeverity] = useState("");
  const [incidentResponsible, setIncidentResponsible] = useState("");
  const [incidentType, setIncidentType] = useState("");
  const [incidentDescription, setIncidentDescription] = useState("");
  const [autoSeverity, setAutoSeverity] = useState(null);
  const [autoRequiredDepts, setAutoRequiredDepts] = useState([]);
  const [severityOverridden, setSeverityOverridden] = useState(false);
  const [submittingIncident, setSubmittingIncident] = useState(false);

  // Cuando cambia el tipo de incidencia, auto-asignar criticidad y departamentos
  useEffect(() => {
    if (incidentType) {
      const sv = getAutoSeverity(incidentType);
      const depts = getRequiredDepartments(incidentType);
      setAutoSeverity(sv);
      setAutoRequiredDepts(depts);
      if (!severityOverridden) {
        setIncidentSeverity(sv);
      }
    } else {
      setAutoSeverity(null);
      setAutoRequiredDepts([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentType]);

  const resetForm = () => {
    setIncidentSeverity("");
    setIncidentResponsible("");
    setIncidentType("");
    setIncidentDescription("");
    setAutoSeverity(null);
    setAutoRequiredDepts([]);
    setSeverityOverridden(false);
  };

  const handleIncidentSubmit = async (e) => {
    e.preventDefault();

    if (!incidentType || !incidentDescription.trim()) {
      showToast(t("Por favor completa todos los campos requeridos"), false);
      return;
    }

    setSubmittingIncident(true);
    let response = null;
    try {
      response = await createPackageIncident({
        packageId,
        severity: incidentSeverity || undefined,
        responsible: incidentResponsible || undefined,
        type: incidentType,
        description: incidentDescription.trim(),
        // Sin origen el payload queda idéntico al de siempre (sin la clave).
        ...(origin ? { origin } : {}),
      });

      if (response.success) {
        showToast(t("Incidencia creada correctamente"), true);
        resetForm();
      } else {
        showToast(response.error?.message || t("Error al guardar la incidencia"), false);
      }
    } catch (error) {
      console.error("Error submitting incident:", error);
      showToast(t("Error al guardar la incidencia"), false);
    } finally {
      setSubmittingIncident(false);
    }

    // Fuera del try a propósito: si el callback del contenedor falla, la
    // incidencia YA existe, y avisar "Error al guardar la incidencia" invitaría
    // a crearla de nuevo. Un error ahí queda como error no capturado.
    if (response?.success) {
      onCreated?.(response.data);
    }
  };

  if (hidden) {
    return null;
  }

  const typeId = `${fieldId}-type`;
  const severityId = `${fieldId}-severity`;
  const responsibleId = `${fieldId}-responsible`;
  const descriptionId = `${fieldId}-description`;

  return (
    <form onSubmit={handleIncidentSubmit} className="package-logbook-form">
      <div className="package-logbook-form__section">
        <h3 className="package-logbook-form__section-title">{t("Abrir incidencia")}</h3>

        {/* Tipo de incidencia - agrupado por categoría */}
        <div className="package-logbook-form__field">
          <label className="package-logbook-form__label" htmlFor={typeId}>
            {t("Tipo de incidencia")} <span className="required">*</span>
          </label>
          <select
            id={typeId}
            className="package-logbook-form__select"
            value={incidentType}
            onChange={(e) => {
              setIncidentType(e.target.value);
              setSeverityOverridden(false);
            }}
            required
          >
            <option value="">{t("Selecciona un tipo")}</option>
            {Object.entries(INCIDENT_TYPES_BY_CATEGORY).map(([catKey, types]) => (
              <optgroup key={catKey} label={t(INCIDENT_CATEGORIES[catKey] || catKey)}>
                {Object.entries(types).map(([typeKey, typeLabel]) => (
                  <option key={typeKey} value={typeKey}>{t(typeLabel)}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Chips informativos: departamentos requeridos */}
        {autoRequiredDepts.length > 0 && (
          <div style={{ marginBottom: "0.5rem" }}>
            <span style={{ fontSize: "0.72rem", color: "#6c757d", fontWeight: 600, textTransform: "uppercase" }}>
              <Info size={12} style={{ marginRight: "0.25rem" }} aria-hidden="true" />
              {t("Departamentos requeridos:")}
            </span>
            <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginTop: "0.3rem" }}>
              {autoRequiredDepts.map((d) => (
                <span key={d} style={{
                  background: "#e9ecef",
                  borderRadius: "12px",
                  padding: "0.15rem 0.6rem",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#495057",
                }}>
                  {t(DEPARTMENT_LABELS[d] || d)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Criticidad (auto-asignada, editable) */}
        <div className="package-logbook-form__field">
          <label className="package-logbook-form__label" htmlFor={severityId}>
            {t("Criticidad")}{autoSeverity && !severityOverridden ? t(" (auto-asignada)") : ""}
          </label>
          <select
            id={severityId}
            className="package-logbook-form__select"
            value={incidentSeverity}
            onChange={(e) => {
              setIncidentSeverity(e.target.value);
              setSeverityOverridden(true);
            }}
          >
            <option value="">{t("Selecciona una criticidad")}</option>
            {Object.entries(INCIDENT_SEVERITY).map(([key, label]) => (
              <option key={key} value={key}>{t(label)}</option>
            ))}
          </select>
        </div>

        {/* Responsable */}
        <div className="package-logbook-form__field">
          <label className="package-logbook-form__label" htmlFor={responsibleId}>{t("Responsable")}</label>
          <select
            id={responsibleId}
            className="package-logbook-form__select"
            value={incidentResponsible}
            onChange={(e) => setIncidentResponsible(e.target.value)}
          >
            <option value="">{t("Sin responsable específico")}</option>
            {Object.entries(INCIDENT_RESPONSIBLE).map(([key, label]) => (
              <option key={key} value={key}>{t(label)}</option>
            ))}
          </select>
        </div>

        {/* Descripción */}
        <div className="package-logbook-form__field">
          <label className="package-logbook-form__label" htmlFor={descriptionId}>
            {t("Descripción")} <span className="required">*</span>
          </label>
          <textarea
            id={descriptionId}
            className="package-logbook-form__textarea"
            value={incidentDescription}
            onChange={(e) => setIncidentDescription(e.target.value)}
            placeholder={t("Describe la incidencia...")}
            rows={4}
            required
          />
        </div>

        {/* Botón enviar */}
        <button
          type="submit"
          className="package-logbook-form__submit"
          disabled={submittingIncident || !incidentType || !incidentDescription.trim()}
        >
          {submittingIncident ? (
            <>
              <Spinner />
              <span>{t("Guardando...")}</span>
            </>
          ) : (
            <>
              <AlertTriangle size={16} aria-hidden="true" />
              <span>{t("Crear incidencia")}</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}
